import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { assignHeat, fetchHeatCatalog, MyApiError, type HeatCatalog, type HeatOption, type Readiness } from '../data/myApi'
import { bumpReadinessRefresh } from '../data/getReady'
import { useShellChrome } from '../components/Shell'
import { useLocale } from '../i18n/useLocale'

/*
 * HEAT SELECTION — V1.9.1 P4 rebuild to the measured design (handoff README
 * §The flows → D. Heat selection + visual-reference/18-heat-selection.png).
 *
 * Presentation: the SHARED flow chrome (README §Flows intro) — header with a
 * back chevron (direction-swapped in code), the event name, and the mono event
 * date at the end (the heat flow has no step counter/progress bar in the
 * measured design); a scrolling body with the ticket-type kicker, H1, intro,
 * live availability count and the option rows; the amber blocked-reason bar
 * DIRECTLY above the footer button; footer `Back` + `Confirm heat` (or `Move
 * to this heat` when changing an existing choice).
 *
 * Behaviour preserved from the battle-tested screen (this is a PRESENTATION
 * rebuild — the data layer stays):
 *  - type-scoped catalogue read (fetchHeatCatalog with idToken + ticketId);
 *    heats belong to a ticket type, served by manage.trumin via my-api.
 *  - FULL heats render the FULL pill, greyed + non-selectable (README §D).
 *  - click = SELECT only (never POSTs); Confirm performs the POST.
 *  - free re-selection: a completed step reopens preselected on the current
 *    heat (the Get-ready row's Edit affordance lands here) and confirms a move
 *    to any available heat at no charge.
 *  - FIRST-PICKER-WINS team lock (BOARDROOM-230739): a locked unit renders the
 *    team heat READ-ONLY with the first-picker note; a divergent write races
 *    to the server's heat_locked/409, which reloads the catalogue instead of
 *    erroring.
 *  - honest loading / empty / load-error states — nothing fabricated: rows
 *    render only the fields the wire carries (PROCESS-3). The wire has ONE
 *    start time per heat and no per-option day (README §D "day + start-time
 *    range" — the day renders once in the header from eventStartsAt); the
 *    "Wave {n} · {x} people" meta stays REMOVED per the CMT-2026-08-11-081455
 *    live-test fix (repeated identical ordinals made rows indistinguishable).
 */

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Canon "Sun 16 Aug 2026". UTC-based — the wire has no event-local timezone
 *  yet (named gap); UTC keeps the render deterministic rather than
 *  viewer-timezone-dependent. */
export function formatEventDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${DAY[d.getUTCDay()]} ${d.getUTCDate()} ${MON[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

function isFull(h: HeatOption): boolean {
  return h.spotsRemaining != null && h.spotsRemaining <= 0
}

// ---------------------------------------------------------------------------
// Styles (measured from the prototype heatVals — README §Design tokens)
// ---------------------------------------------------------------------------

const eyebrowStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 4,
}
const h1Style: CSSProperties = {
  fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 6px',
}
const introStyle: CSSProperties = {
  fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 14px',
}
/** Option row: `minmax(0,1fr) auto` grid, 12px radius; full rows greyed,
 *  the selected row teal-tinted (state colour — README §Status colours). */
const rowStyle = (full: boolean, on: boolean): CSSProperties => ({
  display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', columnGap: 10,
  alignItems: 'center', width: '100%', textAlign: 'start', fontFamily: 'inherit',
  padding: '12px 13px', marginBottom: 8, borderRadius: 12, minHeight: 44,
  ...(full
    ? { background: 'rgba(255,255,255,.02)', border: '1px solid var(--hairline-07)', cursor: 'not-allowed' }
    : on
      ? { background: 'rgba(18,196,172,.1)', border: '1px solid rgba(18,196,172,.5)', cursor: 'pointer' }
      : { background: 'var(--bg-frame)', border: '1px solid var(--hairline-12)', cursor: 'pointer' }),
})
const timeStyle = (full: boolean): CSSProperties => ({
  display: 'block', fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em',
  // Disabled-text token (#4a524d — README §Text) for a FULL row's time.
  color: full ? 'var(--tru-text-disabled)' : 'var(--text)',
})
/** Availability pill: FULL (error-tint) / `{left} OF {cap} LEFT` — amber at
 *  1 left, else teal. Mono, uppercase (README §Typography). */
const pillStyle = (tone: 'full' | 'low' | 'open'): CSSProperties => ({
  fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.07em',
  textTransform: 'uppercase', borderRadius: 5, padding: '4px 7px',
  textAlign: 'center', whiteSpace: 'nowrap',
  ...(tone === 'full'
    ? { color: '#8f6b64', border: '1px solid rgba(229,83,61,.3)', background: 'rgba(229,83,61,.08)' }
    : tone === 'low'
      ? { color: 'var(--warn)', border: '1px solid rgba(230,193,90,.35)' }
      : { color: 'var(--accent-soft)', border: '1px solid rgba(18,196,172,.3)' }),
})
const backBtnStyle: CSSProperties = {
  flex: 'none', border: '1px solid var(--hairline-14)', background: 'transparent',
  color: 'var(--text-softbtn)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
  cursor: 'pointer', minHeight: 48, borderRadius: 12, padding: '0 16px',
}
const ctaStyle = (ok: boolean): CSSProperties => ({
  flex: 1, border: 0, borderRadius: 12, minHeight: 48, background: 'var(--accent)',
  color: 'var(--accent-ink)', fontSize: 15, fontWeight: 800, fontFamily: 'inherit',
  ...(ok ? { cursor: 'pointer' } : { opacity: 0.35, cursor: 'not-allowed' }),
})
const noteStyle: CSSProperties = {
  fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.5, marginTop: 12,
  paddingTop: 12, borderTop: '1px solid var(--hairline-07)',
}

function HeatRow({ heat, selected, onSelect }: {
  heat: HeatOption
  selected: boolean
  onSelect: () => void
}) {
  const { t } = useLocale()
  const full = isFull(heat)
  // The TIME is the primary, distinguishing label (CMT-2026-08-11-081455,
  // Duncan live test — the repeated "Wave 1" meta stays removed). When the
  // wire has no start_time, a neutral placeholder renders — never a
  // fabricated time.
  const timeLabel = heat.startTime || heat.label || t('heat.row.tbc')
  const showPill = full || (heat.spotsRemaining != null && heat.capacity != null)
  const tone: 'full' | 'low' | 'open' = full ? 'full' : (heat.spotsRemaining ?? 2) <= 1 ? 'low' : 'open'

  return (
    <button
      onClick={full ? undefined : onSelect}
      disabled={full}
      aria-pressed={selected}
      aria-disabled={full || undefined}
      style={rowStyle(full, selected)}
    >
      <span style={{ minWidth: 0 }}>
        <span style={timeStyle(full)}>{timeLabel}</span>
      </span>
      {showPill && (
        <span style={pillStyle(tone)}>
          {full ? t('heat.pill.full') : t('heat.pill.left', { left: heat.spotsRemaining ?? 0, cap: heat.capacity ?? 0 })}
        </span>
      )}
    </button>
  )
}

export function HeatSelect({
  eventName,
  eventId,
  idToken,
  base,
  ticketId,
  onBack,
  onAssigned,
}: {
  eventName: string
  eventId: string
  idToken: string
  base: string
  ticketId: string
  onBack: () => void
  onAssigned: (readiness: Readiness) => void
}) {
  const { isMobile } = useShellChrome()
  const { t, dir } = useLocale()
  // Back chevron encodes direction — swapped in code, never CSS-mirrored
  // (README §RTL).
  const backChev = dir === 'rtl' ? '›' : '‹'

  const [catalog, setCatalog] = useState<HeatCatalog | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchHeatCatalog(base, eventId, { idToken, ticketId })
      .then((c) => {
        if (cancelled) return
        setCatalog(c)
        // FREE re-selection: a completed heat step reopens preselected on the
        // current heat; choosing another available heat carries no charge.
        if (c.context?.assignedHeatId) setSelected(c.context.assignedHeatId)
      })
      .catch(() => { if (!cancelled) setLoadError(true) })
    return () => { cancelled = true }
  }, [base, eventId, idToken, ticketId])

  const heats = catalog?.heats ?? null
  const context = catalog?.context ?? null
  const typeName = context?.ticketTypeName ?? null
  const eventDate = context?.eventStartsAt ? formatEventDate(context.eventStartsAt) : null

  const openCount = useMemo(() => (heats ?? []).filter((h) => !isFull(h)).length, [heats])

  // FIRST-PICKER-WINS (BOARDROOM-230739): a TEAM shares one heat. Once any member
  // has set it, every member inherits it LOCKED — no independent pick (the server
  // also rejects a divergent write, heat_locked/409). A team member who is the
  // FIRST picker still chooses, but for the WHOLE team. Optional fields degrade to
  // the old free picker against a pre-deploy my-api that doesn't send them.
  const isTeam = context?.isTeam ?? false
  const unitLocked = context?.unitLocked ?? false
  const lockedHeat = unitLocked
    ? (heats ?? []).find((h) => h.heatId === context?.assignedHeatId) ?? null
    : null

  // CHANGE mode (README §D Edit affordance): a heat is already assigned and
  // the unit is free to move — heading/intro/CTA read as a move, and the
  // current heat is the preselection.
  const currentHeat = !unitLocked && context?.assignedHeatId
    ? (heats ?? []).find((h) => h.heatId === context.assignedHeatId) ?? null
    : null
  const changing = currentHeat !== null
  const currentTime = currentHeat?.startTime || currentHeat?.label || null

  const confirm = async () => {
    if (!selected) return
    setStatus('submitting')
    setError(null)
    try {
      const readiness = await assignHeat(idToken, base, ticketId, selected)
      onAssigned(readiness)
      // Signal the Get-ready home to re-read server readiness immediately, so the
      // heat step flips to done without a manual refresh (CMT-121325 stale-state;
      // parity with the profile-save + waiver-sign bumps).
      bumpReadinessRefresh()
    } catch (e) {
      // FIRST-PICKER-WINS race: another member set the team heat between load and
      // submit → server 409 heat_locked. Don't show a raw error — reload the
      // catalog so the now-locked team heat renders read-only, and clear status.
      if (e instanceof MyApiError && e.code === 'heat_locked') {
        try {
          const c = await fetchHeatCatalog(base, eventId, { idToken, ticketId })
          setCatalog(c)
          if (c.context?.assignedHeatId) setSelected(c.context.assignedHeatId)
          setStatus('idle')
          setError(null)
          return
        } catch {
          /* fall through to the generic message */
        }
      }
      setError(e instanceof Error ? e.message : t('heat.assignFailed'))
      setStatus('error')
    }
  }

  // --- flow chrome geometry (same flow frame as the waiver/profile flows:
  // the shell content padding is cancelled so the flow fills edge-to-edge) ---
  const pad = isMobile
    ? { margin: '-18px -16px -26px', height: 'calc(100% + 44px)' }
    : { margin: '-26px -30px -36px', height: 'calc(100% + 62px)' }
  const headPad = isMobile ? '12px 16px 10px' : '18px 26px 12px'
  const bodyPad = isMobile ? '16px 16px 20px' : '20px 26px 24px'
  const footPad = isMobile ? '10px 16px 12px' : '14px 26px 16px'
  const blockPadX = isMobile ? 16 : 26

  const pickable = !unitLocked && heats !== null && heats.length > 0
  // Blocked-reason contract (README §Flows): the amber bar sits directly above
  // the footer button, naming the specific missing thing — here, the heat pick.
  const showBlock = pickable && selected === null

  return (
    <div id="heat.root" style={{ ...pad, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* HEADER — back chevron, event name, mono event date (README §D: no
          step counter — heat selection is a single-screen flow) */}
      <div id="heat.head" style={{ flex: 'none', padding: headPad, borderBottom: '1px solid var(--hairline-07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <button onClick={onBack} aria-label={eventName} style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: 0 }}>{backChev}</button>
          <span data-testid="heat-context" style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{eventName}</span>
          {eventDate && (
            <span data-testid="heat-date" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', flex: 'none' }}>{eventDate}</span>
          )}
        </div>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: bodyPad }}>
        {/* Ticket-type kicker (mono uppercase) + H1 + intro. The type name is
            wire data (manage.trumin owns heats per ticket type). */}
        {typeName && <div data-testid="heat-kicker" style={eyebrowStyle}>{typeName.toUpperCase()}</div>}
        <h1 style={h1Style}>{changing ? t('heat.heading.change') : t('heat.heading.choose')}</h1>
        <p style={introStyle}>
          {changing
            ? currentTime
              ? t('heat.intro.change', { time: currentTime })
              : t('heat.intro.changeNoTime')
            : typeName
              ? t('heat.intro.choose', { type: typeName })
              : t('heat.intro.chooseNoType')}
        </p>

        {heats === null && !loadError && (
          <p role="status" style={introStyle}>{t('heat.loading')}</p>
        )}
        {loadError && (
          <p role="alert" style={introStyle}>{t('heat.error')}</p>
        )}
        {!unitLocked && heats !== null && heats.length === 0 && (
          // Honest empty state — the organiser hasn't published heats yet;
          // nothing is fabricated, the list works the moment heats are set.
          <div role="status" style={{ border: '1px dashed var(--text-faint)', borderRadius: 12, padding: 20, color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5 }}>
            {t('heat.empty', { event: eventName })}
          </div>
        )}

        {/* FIRST-PICKER-WINS lock — team heat already chosen: read-only, no
            picker. Rendered even if the heat list itself is empty, since the
            team's heat is already set on the unit. */}
        {unitLocked && (
          <div
            data-testid="heat-locked"
            style={{ border: '1px solid var(--accent)', background: 'var(--tint-teal)', borderRadius: 12, padding: '14px 16px' }}
          >
            <span style={{ ...eyebrowStyle, display: 'block' }}>{t('heat.locked.kicker')}</span>
            <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>
              {lockedHeat?.startTime || lockedHeat?.label || t('heat.row.tbc')}
            </span>
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '4px 0 0' }}>
              {t('heat.locked.note')}
            </p>
          </div>
        )}

        {pickable && (
          <>
            {/* Live availability — derived from the payload, teal (done hue at
                text emphasis, per the measured design). */}
            <div data-testid="heat-count" style={{ fontSize: 11.5, color: 'var(--accent-soft)', fontWeight: 700, marginBottom: 12 }}>
              {t('heat.count', { open: openCount, total: heats.length })}
            </div>

            {/* FIRST-PICKER note: a team member choosing the team's heat for the
                first time is choosing for EVERYONE in the team (it then locks). */}
            {isTeam && (
              <div role="note" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--warn)', marginBottom: 12 }}>
                {t('heat.team.first')}
              </div>
            )}

            {/* Option rows — click selects only, never POSTs. */}
            <div>
              {heats.map((h) => (
                <HeatRow
                  key={h.heatId}
                  heat={h}
                  selected={selected === h.heatId}
                  onSelect={() => setSelected(h.heatId)}
                />
              ))}
            </div>

            <div style={noteStyle}>{t('heat.note')}</div>
          </>
        )}

        {error && (
          <span role="alert" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--error)', marginTop: 10 }}>{error}</span>
        )}
      </div>

      {/* BLOCKED-REASON BAR — directly above the footer button, the footer's
          own horizontal padding (README blocked-reason contract; QA §9). */}
      {showBlock && (
        <div id="heat.block" data-testid="heat-block" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: `10px ${blockPadX}px`, background: 'rgba(230,193,90,.1)', borderTop: '1px solid rgba(230,193,90,.28)' }}>
          <span style={{ flex: 'none', color: 'var(--warn)', fontSize: 13 }}>⚠</span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--warn)', fontWeight: 700 }}>{t('heat.block')}</span>
        </div>
      )}

      {/* FOOTER — Back + Confirm heat / Move to this heat (locked units get
          Back only: there is nothing to confirm). */}
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: footPad, borderTop: '1px solid var(--hairline-07)', background: 'var(--bg-frame)' }}>
        <button onClick={onBack} style={unitLocked ? { ...backBtnStyle, flex: 1 } : backBtnStyle}>{t('flow.back')}</button>
        {!unitLocked && (
          <button
            onClick={() => { void confirm() }}
            disabled={selected === null || status === 'submitting'}
            style={ctaStyle(selected !== null && status !== 'submitting')}
          >
            {changing ? t('heat.confirm.move') : t('heat.confirm')}
          </button>
        )}
      </div>
    </div>
  )
}
